/**
 * ProgressBar molecule — preferred public progress API (labels, tags, icons).
 *
 * Prefer: `@/components/ui/molecules/ProgressBar` (or molecules barrel).
 * Atom (`atoms/ProgressBar`) is bar-only for composition / rare bare meters.
 *
 * Composes ProgressBar atom + Typography, Tag, IconWrapper.
 * Variants: default | inline-label | floating-tag | with-status.
 * labelPosition top | side applies to default, inline-label, floating-tag (not with-status).
 *
 * @example
 * <ProgressBar value={50} max={100} variant="inline-label" label="Storage" />
 * <ProgressBar isIndeterminate variant="with-status" statusText="Loading..." />
 */

import React from 'react';
import { Clock } from 'lucide-react';
import { cn } from '@/utils/cn';
import { IconWrapper, ProgressBar as ProgressBarAtom, Typography } from '@/components/ui/atoms';
import { getSemanticLevel } from '../../atoms/ProgressBar/ProgressBar.styles';
import { iconSizeBySize, textVariantBySize } from './ProgressBar.styles';
import {
  getFloatingTagPosition,
  getProgressState,
  isLabelSide,
} from './ProgressBar.utils';
import {
  FloatingTag,
  LabelText,
  SideLayout,
  TopLabelRow,
  ValueText,
} from './ProgressBar.pieces';
import type { ProgressBarIntent, ProgressBarProps } from './ProgressBar.types';

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  variant = 'default',
  size = 'sm',
  palette = 'standard',
  semantic = 'usage',
  intent,
  label,
  labelPosition = 'top',
  valueLabel,
  statusText,
  timeRemaining,
  isIndeterminate = false,
  overBackground = false,
  'aria-label': ariaLabel,
  className = '',
}) => {
  const state = getProgressState(value, max, isIndeterminate, label, valueLabel, ariaLabel);
  const {
    percent,
    indeterminate,
    displayValue,
    ariaLabel: resolvedAriaLabel,
  } = state;
  const resolvedIntent: ProgressBarIntent = intent ?? getSemanticLevel(percent, semantic);
  const textVariant = textVariantBySize[size];
  const iconSize = iconSizeBySize[size];
  /** labelPosition only applies to default, inline-label, floating-tag; with-status always top */
  const side = variant !== 'with-status' && isLabelSide(labelPosition);

  const bar = (
    <ProgressBarAtom
      value={value}
      max={max}
      isIndeterminate={indeterminate}
      size={size}
      palette={palette}
      semantic={semantic}
      intent={resolvedIntent}
      overBackground={overBackground}
      aria-label={resolvedAriaLabel}
    />
  );

  const labelEl = label ? (
    <LabelText variant={textVariant} className={side ? 'shrink-0' : ''}>
      {label}
    </LabelText>
  ) : null;

  const valueEl = displayValue ? (
    <ValueText variant={textVariant} className={side ? 'shrink-0' : ''}>
      {displayValue}
    </ValueText>
  ) : null;

  if (variant === 'default') {
    if (side && (labelEl || valueEl)) {
      return (
        <SideLayout label={labelEl} value={valueEl} bar={bar} className={className} />
      );
    }
    return (
      <div className={cn('w-full space-y-1', className)}>
        {(labelEl || valueEl) && <TopLabelRow label={labelEl} value={valueEl} />}
        {bar}
      </div>
    );
  }

  if (variant === 'inline-label') {
    const rightContent = valueLabel ?? (indeterminate ? undefined : `${percent}%`);
    const valueInline = rightContent ? (
      <ValueText variant={textVariant} className={side ? 'shrink-0' : ''}>
        {rightContent}
      </ValueText>
    ) : null;

    if (side && (labelEl || valueInline)) {
      return (
        <SideLayout
          label={labelEl}
          value={valueInline}
          bar={bar}
          className={className}
        />
      );
    }
    return (
      <div className={cn('w-full space-y-1', className)}>
        <TopLabelRow label={labelEl} value={valueInline} />
        {bar}
      </div>
    );
  }

  if (variant === 'floating-tag') {
    const barWithTag = (
      <div className="relative overflow-visible flex-1 min-w-0">
        {!indeterminate && (
          <FloatingTag
            percent={percent}
            intent={resolvedIntent}
            palette={palette}
            positionStyle={getFloatingTagPosition(percent)}
          />
        )}
        {bar}
      </div>
    );

    if (side && labelEl) {
      return (
        <SideLayout label={labelEl} bar={barWithTag} className={className} />
      );
    }
    return (
      <div className={cn('w-full flex flex-col gap-1', className)}>
        {labelEl}
        {barWithTag}
      </div>
    );
  }

  if (variant === 'with-status') {
    return (
      <div className={cn('w-full space-y-1', className)}>
        <div className="flex justify-between items-center">
          {statusText && (
            <Typography as="p" variant={textVariant} color="primary">
              {statusText}
            </Typography>
          )}
          {!indeterminate && valueEl}
        </div>
        {bar}
        {timeRemaining && (
          <Typography
            as="span"
            variant={textVariant}
            color="secondary"
            className="flex items-center gap-1.5"
          >
            <IconWrapper size={iconSize} aria-hidden>
              <Clock />
            </IconWrapper>
            {timeRemaining}
          </Typography>
        )}
      </div>
    );
  }

  return <div className={cn('w-full', className)}>{bar}</div>;
};
